import { create } from 'zustand'

interface AuthUser {
  id: string
  email: string
}

interface AuthState {
  token: string | null
  user: AuthUser | null
  setSession: (session: { token: string; user: AuthUser }) => void
  clear: () => void
}

const STORAGE_KEY = 'todux.auth.v1'

function loadFromStorage(): { token: string | null; user: AuthUser | null } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { token: null, user: null }
    const parsed = JSON.parse(raw) as { token?: unknown; user?: unknown }
    const token = typeof parsed.token === 'string' ? parsed.token : null
    const user =
      parsed.user &&
      typeof (parsed.user as AuthUser).id === 'string' &&
      typeof (parsed.user as AuthUser).email === 'string'
        ? (parsed.user as AuthUser)
        : null
    return { token, user }
  } catch {
    return { token: null, user: null }
  }
}

const initial = loadFromStorage()

export const useAuthStore = create<AuthState>()((set) => ({
  token: initial.token,
  user: initial.user,

  setSession: ({ token, user }) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, user }))
    set({ token, user })
  },

  clear: () => {
    localStorage.removeItem(STORAGE_KEY)
    set({ token: null, user: null })
  },
}))

// Selector hooks — call these inside React components
export const useToken = () => useAuthStore((s) => s.token)
export const useCurrentUser = () => useAuthStore((s) => s.user)
export const useIsAuthenticated = () => useAuthStore((s) => s.token !== null)
